import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";

import { auth } from "../utils/firebase.utils";

/**
 * Who is signed in, for every component, from one Firebase listener.
 *
 * Replaces reads of `auth.currentUser` during render (which never re-render
 * when it changes) and of `localStorage.accessToken` (which went stale: it was
 * written once at sign-in and never refreshed, so it said "signed in" long
 * after the token behind it had expired).
 *
 * `status` starts at "loading" while Firebase restores a persisted session.
 * Open routes never wait on it; gated ones show a spinner until it settles, so
 * a signed-in user reloading a gated page never sees the sign-in prompt.
 *
 * Imperative code that has to know *right now* — a resumed action, the axios
 * interceptor — reads `auth.currentUser` instead: this state lags the
 * listener by a render.
 */
const AuthContext = createContext({ status: "loading", user: null });

export const AuthProvider = ({ children }) => {
  const [state, setState] = useState({ status: "loading", user: null });
  useEffect(
    () =>
      onAuthStateChanged(auth, (user) =>
        setState({ status: user ? "signed-in" : "signed-out", user }),
      ),
    [],
  );
  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
